// feature_tracking — the non-media Amplitude vocabulary for v2.
//
// WHY THIS FILE EXISTS
// Legacy emitted a family of `Feature: *` / `Auth: *` / `Promotions: *` /
// `Gallery: *` events from webpack views. v2 rebuilt those surfaces as behaviors
// and carried over only three of them (`Feature: Navigation Bar`,
// `Feature: Homepage Item`, `Feature: Special Offer`), so the rest had no v2
// emitter at all — parity validation P-9c rows 9-19.
//
// Names, prop KEYS and prop VALUES are transcribed from the legacy emitters cited
// per constant below. Amplitude charts key on these exact strings — do not tidy
// them (note `Top 10 Gallery: Item View` is snake_case in its props while
// everything else is lowerCamel: that asymmetry is legacy's, and it is deliberate).
//
// One module so a name or a shared prop rule lives in exactly one place; each
// behavior imports what it needs and calls trackFeature().

export const FEATURE_EVENTS = {
  // webpack/components/navbar/{menu,dropdown,mobile-subnav}.coffee — already
  // emitted by behaviors/track_clicks.js.
  NAVIGATION_BAR: 'Feature: Navigation Bar',
  // webpack/views/homepage.js — already emitted by behaviors/track_items.js.
  HOMEPAGE_ITEM: 'Feature: Homepage Item',
  // app/helpers/event_tracker_helper.rb#track_tag_quicklink_click (server renders
  // data-track-name / data-track-params; a delegated client handler fires it).
  TAG_QUICKLINK: 'Feature: Tag Quicklink Click',
  // webpack/features/search_modal/views/results.coffee:131,
  // webpack/features/search_modal_v2/search_modal.jsx:73,
  // webpack/views/search/base.coffee:40, views/shared/browse_section.coffee:132
  SEARCH_TERMS: 'Feature: Search Terms',
  // webpack/views/shared/browse_section.coffee:146 (sort) and
  // views/shared/facets_flyout.coffee:138 (facets — 'Add' only, never 'Remove')
  ADVANCED_SEARCH_FILTER: 'Feature: Advanced Search Filter',
  // webpack/content_rating/views/button.coffee:98
  CONTENT_RATING: 'Feature: Content Rating',
  // webpack/favorites/views/favoriting_control.coffee:215 / :221
  ADD_FAVORITE: 'Feature: Add Favorite',
  REMOVE_FAVORITE: 'Feature: Remove Favorite',
  // webpack/components/login_modal.coffee:103
  LOGIN_MODAL: 'Feature: Login Modal',
  // webpack/components/step_down_offer_modal.coffee:161
  STEP_DOWN_OFFER_MODAL: 'Feature: Step Down Offer Modal',
  // webpack/promotions/whackit-bracket/promotion/round.coffee:28
  BRACKET_VOTE: 'Promotions: Whackit Bracket Vote',
  // webpack/views/blog/post/gallery.coffee:40
  GALLERY_ITEM_VIEWED: 'Gallery: Item Viewed',
  // webpack/views/blog/post/top10.coffee:55
  TOP10_ITEM_VIEW: 'Top 10 Gallery: Item View',
};

// `Feature: <type> Modal` — webpack/components/access_modal.coffee:46 built the
// name from the modal's content type, so the name itself is data.
export const accessModalEvent = (type) => `Feature: ${type} Modal`;

// `Feature: Device Authorization <type>` — webpack/account/device_auth.coffee:128.
export const deviceAuthEvent = (type) => `Feature: Device Authorization ${type}`;

// `SK.EventTracking` is set up by its own entrypoint, and behaviors are not
// ordered against it. Almost every event here fires on a user interaction, long
// after boot — but not all: a `?g=&i=` gallery deep link opens the viewer during
// behavior init, and an optional-chained call there silently DROPS the event
// (observed: the deep-linked `Gallery: Item Viewed` never fired). The server's
// `window._sktrk` queue is no help — EventTracker consumes it at construction.
//
// So calls made before the tracker exists are buffered here and flushed on the
// entrypoint's ready event. Checking-then-subscribing cannot race: both happen in
// one synchronous block, so the entrypoint cannot slip in between.
export const TRACKER_READY_EVENT = 'sk:event-tracking-ready';

const pending = [];
let subscribed = false;

function whenTracker(fn) {
  const tracker = window.SK?.EventTracking;
  if (tracker) {
    fn(tracker);
    return;
  }

  pending.push(fn);
  if (subscribed) return;
  subscribed = true;
  document.addEventListener(TRACKER_READY_EVENT, () => {
    const ready = window.SK?.EventTracking;
    if (!ready) return;
    for (const queued of pending.splice(0)) queued(ready);
  }, { once: true });
}

export function trackFeature(name, props = {}, cb = null) {
  whenTracker((tracker) => tracker.track?.(name, props, cb));
}

export function setUserProperty(key, value) {
  whenTracker((tracker) => tracker.setUserProperty?.(key, value));
}

// Split-test membership is a user PROPERTY, not an event: EventTracker#
// setSplitTestProperty stores `key = "<name>_<version>"` through an Amplitude
// `Identify`, so every later event from this device is segmented by it. That
// makes ORDER load-bearing — the property has to reach Amplitude before the
// events it is meant to split, or those events land unsegmented and the test's
// charts under-count the variant.
//
// Two mechanisms already keep that order and neither needs re-implementing
// here: whenTracker's `pending` buffer flushes FIFO, so pre-boot calls arrive
// in call order; and EventTracker keeps properties (`trackAmplitude`, drained
// at `loaded`) in a separate queue from events (`trackAmplitudeEvent`, drained
// at `ready`, after the properties) — so a property beats an event even across
// the boot boundary. Callers only have to call this one first.
export function setSplitTestProperty(key, name, version) {
  whenTracker((tracker) => tracker.setSplitTestProperty?.(key, name, version));
}

// `searchType` — legacy had TWO derivations and they are not the same, so both are
// reproduced rather than unified:
//
//   browseSearchType()  webpack/views/shared/browse_section.coffee:11 — the bare last
//                       path segment. Used by Feature: Search Terms (browse) and by
//                       the sort dimension of Feature: Advanced Search Filter.
//   facetSearchType()   webpack/views/shared/facets_flyout.coffee:3-9 — the last
//                       segment only when it is one of a whitelist, else 'playlist'
//                       for a -p<id> path and 'video' for a -v<id> path, else
//                       undefined. Used by the FACET dimension of the same event.
const FACET_SEARCH_TYPES = ['clips', 'pics', 'celebs', 'titles', 'video', 'playlist'];

export function browseSearchType() {
  return window.location.pathname.split('/').pop();
}

export function facetSearchType() {
  const last = window.location.pathname.split('/').pop();
  if (FACET_SEARCH_TYPES.includes(last)) return last;
  if (/[a-z0-9\-_]+-p[0-9]+.*?/.test(last)) return 'playlist';
  if (/[a-z0-9\-_]+-v[0-9]+.*?/.test(last)) return 'video';
  return undefined;
}

// Favorites' `actionSource`, transcribed from
// webpack/favorites/views/favoriting_control.coffee:8-18,299-306: the media modal
// wins if it is open, otherwise the FIRST path pattern that matches, otherwise
// 'Other'. Order is significant — the bare `-c\d+$` entries must be tested before
// nothing, and the legacy hash order is preserved here.
const ACTION_SOURCE_MAP = [
  [/\/browse$/, 'Browse Page'],
  [/\/updates$/, 'Updates Page'],
  [/\/search\/celebs$/, 'Search Celebs Page'],
  [/\/search\/titles$/, 'Search Titles Page'],
  [/-c\d+$/, 'Celeb Page'],
  [/-t\d+$/, 'Title Page'],
  [/-p\d+$/, 'Playlist Page'],
  [/-v\d+$/, 'Skin Video Page'],
  [/-c\d+\/titles$/, 'Celeb Page - Titles'],
  [/-t\d+\/celebs$/, 'Title Page - Celebs'],
];

// The ONE favorite emitter. behaviors/favorite.js (detail pages) and
// behaviors/card_favorite.js (grid tiles) have identical toggles, so the event
// lives here rather than in both. Call it only AFTER the server confirms — legacy
// fired from onSaveSuccess / onDestroySuccess, never optimistically.
export function trackFavoriteToggle(button, name) {
  trackFeature(name, {
    favoritedId: Number(button.dataset.favoriteId) || button.dataset.favoriteId,
    favoritedType: button.dataset.favoriteType,
    actionSource: favoriteActionSource(),
  });
}

// `Gallery: Item Viewed` / `Top 10 Gallery: Item View` — ONE emitter, because v2
// renders both surfaces through the same server-rendered lightbox
// (components/_gallery + posts/post_types/_top10_items, behaviors/lightbox.js)
// where legacy had two separate Backbone views.
//
// Their prop shapes DISAGREE and that asymmetry is load-bearing for the existing
// charts, so it is reproduced, not harmonised:
//   gallery.coffee:40  { galleryName, galleryId, itemId }   galleryId = Gallery#id
//   top10.coffee:55    { gallery_id, item_id }              gallery_id = POST id
//                                                           (top10_post_decorator
//                                                            #gallery_json's `id`)
// Both legacy views fired when the viewer became visible AND on every item change
// while it was visible, so a single call from lightbox's show() covers both.
export function trackGalleryItem(root, itemId) {
  const galleryId = root.dataset.galleryId;
  if (!galleryId || !itemId) return;

  const num = (value) => Number(value) || value;

  if (root.dataset.galleryType === 'top10') {
    trackFeature(FEATURE_EVENTS.TOP10_ITEM_VIEW, {
      gallery_id: num(galleryId),
      item_id: num(itemId),
    });
    return;
  }

  trackFeature(FEATURE_EVENTS.GALLERY_ITEM_VIEWED, {
    galleryName: root.dataset.galleryName,
    galleryId: num(galleryId),
    itemId: num(itemId),
  });
}

export function favoriteActionSource() {
  // Legacy checked `$('.sk-media-modal').is(':visible')`; v2's viewer is a
  // <dialog data-media-modal>, so "open" is the equivalent test.
  const modal = document.querySelector('dialog[data-media-modal]');
  if (modal?.open) return 'Media Modal';

  const path = window.location.pathname;
  for (const [re, source] of ACTION_SOURCE_MAP) {
    if (re.test(path)) return source;
  }
  return 'Other';
}
